import React, { ReactElement, useState } from "react";

import { KeyboardArrowDown } from "@material-ui/icons";
import { Menu, MenuItem } from "@portex-pro/ui-components";
import clsx from "clsx";
import values from "lodash/values";
import { useTranslation } from "react-i18next";
import { TrailerType } from "types/TrailerType";

import { containerTypes } from "../const";

interface ContainerTypeButtonGroupViewProps {
  value?: TrailerType; // expected to be a narrowed ContainerType
  onChange?: (value: TrailerType) => void;
  showError?: boolean;
  className?: string;
}

const ContainerTypeTabViewGroup = ({
  value,
  onChange,
  showError = false,
  className,
}: ContainerTypeButtonGroupViewProps): ReactElement => {
  const { t } = useTranslation(["shipper", "common"]);
  const { DRY, REEFER, ...remainingContainerTypes } = containerTypes;
  const visibleTypes = [DRY, REEFER];
  const dropdownTypes = values(remainingContainerTypes);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const isMenuSelected = dropdownTypes.some((t) => t === value);

  return (
    <>
      <div
        className={clsx(
          "flex space-x-1 p-1 rounded-lg w-full",
          className,
          showError && !value ? "bg-red-50 border border-red-300" : "bg-gray-100"
        )}
      >
        {visibleTypes.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onChange?.(type)}
            className={clsx(
              "px-4 py-2 rounded-md text-sm font-medium transition-colors border flex-1",
              value === type
                ? "border-blue-600 text-blue-600 bg-white shadow-sm"
                : "border-transparent text-gray-700 hover:text-gray-900 hover:bg-gray-200"
            )}
          >
            {t(`common:trailerTypes.${type}`)}
          </button>
        ))}
        <button
          id="container-type-button"
          type="button"
          aria-controls="container-type-menu"
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          className={clsx(
            "px-4 py-2 rounded-md text-sm font-medium transition-colors border flex-1 flex items-center justify-center",
            isMenuSelected
              ? "border-blue-600 text-blue-600 bg-white shadow-sm"
              : "border-transparent text-gray-700 hover:text-gray-900 hover:bg-gray-200"
          )}
        >
          <span className="flex-1">
            {value && isMenuSelected ? t(`common:trailerTypes.${value}`) : t("shipper:other")}
          </span>
          <KeyboardArrowDown fontSize="small" />
        </button>
      </div>
      <Menu
        id="container-type-menu"
        aria-labelledby="container-type-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {dropdownTypes.map((type) => (
          <MenuItem
            key={type}
            selected={value === type}
            onClick={() => {
              onChange?.(type);
              handleClose();
            }}
          >
            {t(`common:trailerTypes.${type}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ContainerTypeTabViewGroup;
